import { createContext, useContext, useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { api, unwrap, type ApiError, type KommuneOverview, type OrgRef } from "../../api/client";
import { useMe } from "../../auth/AuthContext";
import { useAttribution } from "../../components/Attribution";
import { Notice } from "../../components/ui";
import { t } from "../../i18n/t";
import { writeLastKommune } from "../../lib/storage";
import { useQuery } from "../../lib/useQuery";
import { NotFoundPage } from "../NotFoundPage";

export interface KommuneContextValue {
  orgId: string;
  nr: string;
  org: OrgRef;
  /** Kommune name from the session (available before the overview loads). */
  navn: string;
  overview: KommuneOverview | undefined;
  overviewError: ApiError | undefined;
  overviewLoading: boolean;
  reloadOverview: () => void;
}

const KommuneContext = createContext<KommuneContextValue | null>(null);

export function useKommune(): KommuneContextValue {
  const ctx = useContext(KommuneContext);
  if (!ctx) throw new Error("useKommune must be used inside <KommuneLayout>");
  return ctx;
}

/**
 * Route element for `/o/:orgId/k/:nr/*`: loads the kommune overview once and shares it with the
 * dashboard, map, tiltaksliste and endringer pages. Shows the synthetic-data banner on all of them.
 */
export function KommuneLayout() {
  const { orgId = "", nr = "" } = useParams();
  const me = useMe();
  const org = me.orgs.find((o) => o.org_id === orgId);
  const kommune = org?.kommuner.find((k) => k.nummer === nr);
  const allowed = !!org && !!kommune;

  const query = useQuery(allowed ? `overview:${orgId}:${nr}` : null, (signal) =>
    unwrap(
      api.GET("/api/orgs/{org_id}/kommuner/{nummer}", {
        params: { path: { org_id: orgId, nummer: nr } },
        signal,
      }),
    ),
  );

  useEffect(() => {
    if (allowed) writeLastKommune({ orgId, nr });
  }, [allowed, orgId, nr]);
  useAttribution(query.data?.attribution);

  if (!org || !kommune) return <NotFoundPage message={t("errors.noAccessKommune")} />;

  const value: KommuneContextValue = {
    orgId,
    nr,
    org,
    navn: query.data?.navn || kommune.navn || nr,
    overview: query.data,
    overviewError: query.error,
    overviewLoading: query.loading,
    reloadOverview: query.reload,
  };

  return (
    <KommuneContext.Provider value={value}>
      {query.data?.run?.synthetic && (
        <Notice kind="synthetic" title={t("dashboard.syntheticTitle")} role="note">
          <p>{t("dashboard.syntheticBody")}</p>
        </Notice>
      )}
      <Outlet />
    </KommuneContext.Provider>
  );
}
